/// <amd-module name="Test/TestResultWorkItems/Store/WorkItemsViewStore" />
import { IColumn } from 'OfficeFabric/DetailsList';
import { removeIndex } from 'OfficeFabric/Utilities';
import {
    IWorkItemAndMaps,
    IWorkItemWithColorsAndIcon,
    WorkItemsActionsHub,
} from 'Test/TestResultWorkItems/Actions/WorkItemsActionHub';
import { WorkItemFields, WorkItemTypes } from 'Test/TestResultWorkItems/Common';
import { IVssPageContext } from 'VSS/Platform/Context';
import { IVssUserClaimsService, UserClaims } from 'VSS/Platform/UserClaims';
import { Store } from 'VSS/Reflux/Store';
import { WorkItem, WorkItemStateColor } from 'Wit/Clients/Wit/Contracts/WorkItemTracking';
import {
    WorkItemTypeColorAndIcon,
    WorkItemTypeColorAndIcons,
} from 'WorkItemsHub/External/NewWorkItem/WorkItemTypeColorAndIconsProvider';

export interface IWorkItemsState {
    errorMessage: string | null;
    isLoading: boolean;
    bugsWorkItems: IWorkItemWithColorsAndIcon[];
    requirementsWorkItems: IWorkItemWithColorsAndIcon[];
    columns: IColumn[];
    hasPublishTestResultsPermission: boolean;
}

export class WorkItemsViewStore extends Store {

    constructor(private pageContext: IVssPageContext, private _actionsHub: WorkItemsActionsHub) {
        super("");
        this._workItems = {};
        this._initialize();
    }

    private _initialize(): void {
        this._state = this._getDefaultState();
        this._actionsHub.onError.addListener(this._onErrorListener);
        this._actionsHub.workItemsLoaded.addListener(this._workItemsLoadedListener);
        this._actionsHub.onSort.addListener(this._workItemsSortedListener);
        this._actionsHub.onDeleteWorkItemLink.addListener(this._afterWorkItemsDeletedListener);
        this._actionsHub.clearState.addListener(this._clearState);
        this._actionsHub.updateWorkItems.addListener(this._updateWorkItemsListener);
    }

    public dispose() {
        this._actionsHub.onError.removeListener(this._onErrorListener);
        this._actionsHub.workItemsLoaded.removeListener(this._workItemsLoadedListener);
        this._actionsHub.onSort.removeListener(this._workItemsSortedListener);
        this._actionsHub.onDeleteWorkItemLink.addListener(this._afterWorkItemsDeletedListener);
        this._actionsHub.clearState.removeListener(this._clearState);
        this._actionsHub.updateWorkItems.removeListener(this._updateWorkItemsListener);
    }

    public getState(): IWorkItemsState {
        return {
            errorMessage: this._state.errorMessage,
            isLoading: this._state.isLoading,
            bugsWorkItems: this._workItems[WorkItemTypes.Bug] ? this._workItems[WorkItemTypes.Bug].slice(0) : [],
            requirementsWorkItems: this._workItems[WorkItemTypes.Requirements] ? this._workItems[WorkItemTypes.Requirements].slice(0) : [],
            columns: this._state.columns,
            hasPublishTestResultsPermission: this._state.hasPublishTestResultsPermission,
        };
    }

    private _clearState = (): void => {
        this._state = this._getDefaultState();
    }

    private _workItemsLoadedListener = (workItemsAndColor: IWorkItemAndMaps): void => {
        this._stateAndColorMap = workItemsAndColor.stateAndColorMap;
        this._colorAndIconMap = workItemsAndColor.colorAndIconMap;
        this._workItems[WorkItemTypes.Bug] = this._fillColorStateIconInWorkItem(workItemsAndColor.bugs);
        this._workItems[WorkItemTypes.Requirements] = this._fillColorStateIconInWorkItem(workItemsAndColor.requirements);
        this._state.isLoading = false;
        this._state.hasPublishTestResultsPermission = this._isMember(); //TCMPermissionUtils.PermissionUtils.hasPublishResultPermission(this._viewContext.data.mainData.project.id);
        this.emitChanged();
    }

    private _fillColorStateIconInWorkItem = (workItems: WorkItem[]): IWorkItemWithColorsAndIcon[] => {
        let workItemWithColorAndState: IWorkItemWithColorsAndIcon[] = [];
        workItems.forEach(workItem => {
            let stateColor: string = "";
            let bugField = this._stateAndColorMap ? this._stateAndColorMap[workItem.fields[WorkItemFields.WorkItemType]] : [];
            for (let bugStates of bugField) {
                if (workItem.fields[WorkItemFields.State] === bugStates.name) {
                    stateColor = bugStates.color;
                    break;
                }
            };
            let color: string = "";
            let icon: string = "";
            for (let workItemType of this._colorAndIconMap ? this._colorAndIconMap : []) {
                if (workItemType.workItemTypeName === workItem.fields[WorkItemFields.WorkItemType]) {
                    color = workItemType.color;
                    icon = WorkItemTypeColorAndIcons.ICON_NAME_MAPPINGS[workItemType.icon];
                    break;
                }
            }
            let workItemWithColor: IWorkItemWithColorsAndIcon = {
                workItem: workItem,
                stateColor: stateColor,
                color: color,
                icon: icon
            };
            workItemWithColorAndState.push(workItemWithColor);
        });
        return workItemWithColorAndState;
    }

    private _workItemsSortedListener = (sortedDataAndType: { workItems: IWorkItemWithColorsAndIcon[], workItemType: WorkItemTypes }): void => {
        this._workItems[sortedDataAndType.workItemType] = sortedDataAndType.workItems;
        this.emitChanged();
    }

    private _afterWorkItemsDeletedListener = (deletedWorkItemsWithType: { workItems: WorkItem[], workItemType: WorkItemTypes }): void => {
        deletedWorkItemsWithType.workItems.forEach((workItem: WorkItem) => {
            let NumberOfBugs = this._workItems[deletedWorkItemsWithType.workItemType].length;
            let index = 0;
            for (; index < NumberOfBugs; ++index) {
                if (this._workItems[deletedWorkItemsWithType.workItemType][index].workItem.id === workItem.id) {
                    break;
                }
            };
            this._workItems[deletedWorkItemsWithType.workItemType] = removeIndex(this._workItems[deletedWorkItemsWithType.workItemType], index);
        });
        this.emitChanged();
    }

    private _updateWorkItemsListener = (updatedWorkItemWithType: { updatedWorkItem: WorkItem, workItemType: WorkItemTypes }): void => {
        let workItems: IWorkItemWithColorsAndIcon[] = this._workItems[updatedWorkItemWithType.workItemType];
        let length: number = workItems.length;
        for (let i = 0; i < length; ++i) {
            if (workItems[i].workItem.id === updatedWorkItemWithType.updatedWorkItem.id) {
                workItems[i] = this._fillColorStateIconInWorkItem([updatedWorkItemWithType.updatedWorkItem])[0];
                break;
            }
        }
        this.emitChanged();
    }

    private _onErrorListener = (errorMessage: string): void => {
        this._state.errorMessage = errorMessage;
        this._state.isLoading = false;
        this.emitChanged();
    }

    private _getDefaultState(): IWorkItemsState {
        return {
            errorMessage: null,
            bugsWorkItems: [],
            requirementsWorkItems: [],
            columns: [],
            isLoading: true,
            hasPublishTestResultsPermission: false,
        };
    }

    private _isMember(): boolean {
        return this.pageContext.getService<IVssUserClaimsService>("IVssUserClaimsService").hasClaim(UserClaims.Member);
    }

    private _state: IWorkItemsState;
    private _stateAndColorMap: { [key: string]: WorkItemStateColor[] } | undefined;
    private _colorAndIconMap: WorkItemTypeColorAndIcon[] | undefined;
    private _workItems: { [key: string]: IWorkItemWithColorsAndIcon[] };
}
